import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  deleteUser,
  disableUser,
  listUsers,
  ManagedUser,
  updateUserAccess,
  UserRole,
} from "../services/api";
import { HidLogo } from "./Icons";

interface AdminUsersPageProps {
  onOpenChat: () => void;
}

function isActive(user: ManagedUser) {
  return user.is_active === true || user.is_active === 1;
}

function detailOf(err: unknown) {
  return err && typeof err === "object" && "response" in err
    ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
    : undefined;
}

export function AdminUsersPage({ onOpenChat }: AdminUsersPageProps) {
  const { user: currentUser, logout } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listUsers());
    } catch {
      setError(
        "Could not load users. Confirm the backend is running and your account has admin access."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const changeAccess = async (
    target: ManagedUser,
    role: UserRole,
    active: boolean
  ) => {
    setBusyId(target.id);
    setError(null);
    try {
      const updated = await updateUserAccess(target.id, role, active);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err: unknown) {
      setError(detailOf(err) || "Could not update this account.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDisable = async (target: ManagedUser) => {
    const who = target.email || target.username;
    if (!window.confirm(`Disable access for ${who}? They keep their history and can be reactivated.`))
      return;

    setBusyId(target.id);
    setError(null);
    try {
      await disableUser(target.id);
      setUsers((prev) =>
        prev.map((u) => (u.id === target.id ? { ...u, is_active: false } : u))
      );
    } catch (err: unknown) {
      setError(detailOf(err) || "Could not disable this account.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (target: ManagedUser) => {
    const who = target.email || target.username;
    if (
      !window.confirm(
        `Permanently delete ${who}?\n\nThis removes their sign-in identity, conversations and uploads. This cannot be undone.`
      )
    )
      return;

    setBusyId(target.id);
    setError(null);
    try {
      await deleteUser(target.id);
      setUsers((prev) => prev.filter((u) => u.id !== target.id));
    } catch (err: unknown) {
      setError(detailOf(err) || "Could not delete this account.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-4">
            <HidLogo className="h-8 w-auto max-w-[120px]" />
            <div>
              <h1 className="text-lg font-bold text-brand-navy">Administration settings</h1>
              <p className="text-xs text-slate-500">Users and application access</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onOpenChat}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Open Chat
            </button>
            <button
              type="button"
              onClick={() => void logout()}
              className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Registered users</h2>
            <p className="mt-1 text-sm text-slate-500">
              New registrations receive standard user access.{" "}
              {isSuperadmin
                ? "As superadmin you can change roles, disable accounts, or delete them permanently."
                : "Administrators can disable accounts. Only a superadmin can change roles or delete accounts."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading users…</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No users registered.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Person</th>
                    <th className="px-4 py-3">Registered</th>
                    <th className="px-4 py-3">Access level</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((managed) => {
                    const active = isActive(managed);
                    const isSelf = managed.id === currentUser?.id;
                    const busy = busyId === managed.id;
                    return (
                      <tr key={managed.id} className={!active ? "bg-slate-50 opacity-70" : ""}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">
                            {managed.display_name || managed.username}
                            {isSelf && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                          </p>
                          <p className="text-xs text-slate-500">
                            {managed.email || managed.username}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">
                          {new Date(managed.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={managed.role}
                            disabled={busy || isSelf || !isSuperadmin}
                            onChange={(e) =>
                              void changeAccess(managed, e.target.value as UserRole, active)
                            }
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:opacity-60"
                          >
                            <option value="user">Standard user</option>
                            <option value="admin">Administrator</option>
                            <option value="superadmin">Superadmin</option>
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-semibold ${
                              active
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            {active ? "Active" : "Disabled"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-4">
                            {active ? (
                              <button
                                type="button"
                                disabled={busy || isSelf}
                                onClick={() => void handleDisable(managed)}
                                className="text-sm font-semibold text-rose-600 hover:text-rose-800 disabled:opacity-40"
                              >
                                Disable
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => void changeAccess(managed, managed.role, true)}
                                className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 disabled:opacity-40"
                              >
                                Reactivate
                              </button>
                            )}

                            {isSuperadmin && (
                              <button
                                type="button"
                                disabled={busy || isSelf}
                                onClick={() => void handleDelete(managed)}
                                className="text-sm font-semibold text-rose-700 underline decoration-rose-300 hover:decoration-rose-700 disabled:opacity-40"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
